import {
    featureFlagService,
    FeatureFlagsEnabledMap,
} from "./featureFlagService";

type CacheEntry = {
    value: boolean;
    fetchedAt: number;
};

/**
 * Feature Flag Client - provides caching layer for feature flag checks
 * Supports both synchronous (cached) and async (fresh) checks
 */
class FeatureFlagClient {
    private cache: Map<string, CacheEntry> = new Map();
    private inFlight: Map<string, Promise<boolean>> = new Map();
    private ttlMs: number;

    constructor(ttlMs: number = 60_000) {
        this.ttlMs = ttlMs;
    }

    /**
     * Synchronous check - returns cached value or false, triggers background refresh
     * Use this for UI guards where you need immediate response
     */
    public isEnabled(key: string): boolean {
        const now = Date.now();
        const cached = this.cache.get(key);
        if (cached && now - cached.fetchedAt < this.ttlMs) {
            return cached.value;
        }

        // Trigger background refresh without awaiting
        void this.isEnabledAsync(key);
        // Return cached value or false (hide by default)
        return cached ? cached.value : false;
    }

    /**
     * Async check - fetches fresh value if cache expired
     * Use this when you need guaranteed fresh data
     */
    public async isEnabledAsync(key: string): Promise<boolean> {
        const now = Date.now();
        const cached = this.cache.get(key);

        if (cached && now - cached.fetchedAt < this.ttlMs) {
            return cached.value;
        }

        // Check if there's already a request in flight
        const existing = this.inFlight.get(key);
        if (existing) {
            return existing;
        }

        const fetchPromise = (async () => {
            try {
                const result: FeatureFlagsEnabledMap =
                    await featureFlagService.getEnabled([key]);
                // Default to false if key doesn't exist
                const value = Boolean(result[key]);
                this.cache.set(key, { value, fetchedAt: Date.now() });
                return value;
            } finally {
                this.inFlight.delete(key);
            }
        })();

        this.inFlight.set(key, fetchPromise);
        return fetchPromise;
    }

    /**
     * Preload multiple flags at once - useful at app startup
     */
    public async preload(keys: string[]): Promise<void> {
        if (!keys || keys.length === 0) return;
        try {
            const result = await featureFlagService.getEnabled(keys);
            const now = Date.now();
            Object.keys(result).forEach((k) => {
                this.cache.set(k, { value: Boolean(result[k]), fetchedAt: now });
            });
        } catch {
            // Silently ignore preload errors
        }
    }

    /**
     * Invalidate cache for specific keys or all keys
     */
    public invalidate(keys?: string[]): void {
        if (!keys) {
            this.cache.clear();
            return;
        }
        keys.forEach((k) => this.cache.delete(k));
    }

    /**
     * Update TTL for cache entries
     */
    public setTtl(ttlMs: number): void {
        this.ttlMs = ttlMs;
    }

    /**
     * Get current TTL value
     */
    public getTtl(): number {
        return this.ttlMs;
    }
}

export const featureFlag = new FeatureFlagClient();
